import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import AddButton, { DraftButton } from "../buttons/button";
import ModalCloseIcon from "../../assets/icons/modal-close-icon";
import SqlEditor from "./sql-editor";

const EXAMPLE_SQL = `(p_from_date DATE DEFAULT NULL, p_to_date DATE DEFAULT NULL)
RETURNS TABLE("item_name" TEXT, "total_qty" NUMERIC)
LANGUAGE sql
AS $$
  SELECT item_name, SUM(quantity) as total_qty
  FROM sales_order_items
  WHERE created_date >= COALESCE(p_from_date, '2000-01-01')
    AND created_date <= COALESCE(p_to_date, CURRENT_DATE)
  GROUP BY item_name
  ORDER BY total_qty DESC
$$`;

const EXAMPLE_PLPGSQL = `(p_item_code TEXT DEFAULT NULL)
RETURNS TABLE("item_name" TEXT, "stock_qty" NUMERIC)
LANGUAGE plpgsql
AS $$
BEGIN
  RETURN QUERY
  SELECT i.item_name, SUM(b.item_sellable_quantity) as stock_qty
  FROM good_order_receipt_items_list i
  JOIN good_order_receipt_items_batches_list b ON b.item_id = i.item_id
  WHERE (p_item_code IS NULL OR i.item_code = p_item_code)
  GROUP BY i.item_name;
END;
$$`;

const FunctionEditorModal = ({
  isOpen,
  onClose,
  onSave,
  onValidate,
  initialData = null,
  isEdit = false,
  loading = false,
  schema = [],
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [functionDefinition, setFunctionDefinition] = useState("");
  const [isShared, setIsShared] = useState(false);
  const [error, setError] = useState(null);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    if (isOpen && initialData) {
      setName(initialData.name || "");
      setDescription(initialData.description || "");
      setFunctionDefinition(initialData.function_definition || "");
      setIsShared(initialData.is_shared || false);
    } else if (isOpen && !initialData) {
      setName("");
      setDescription("");
      setFunctionDefinition("");
      setIsShared(false);
    }
    setError(null);
  }, [isOpen, initialData]);

  const handleValidate = () => {
    setValidating(true);
    setError(null);
    onValidate(
      { function_definition: functionDefinition },
      () => {
        setValidating(false);
        setError(null);
      },
      (err) => {
        setValidating(false);
        setError(err?.data?.message || err?.message || "Validation failed");
      }
    );
  };

  const handleSave = () => {
    if (!name.trim()) {
      setError("Function name is required");
      return;
    }
    if (!functionDefinition.trim()) {
      setError("Function definition is required");
      return;
    }

    setError(null);

    const payload = {
      ...(isEdit && initialData ? { function_id: initialData.function_id } : {}),
      name: name.trim(),
      description: description.trim(),
      function_definition: functionDefinition,
      is_shared: isShared,
    };

    onSave(payload);
  };

  const insertExample = (example) => {
    setFunctionDefinition(example);
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={onClose}>
        <div className="flex min-h-screen items-center justify-center px-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-60" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
          >
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div
                className="px-6 py-4 flex items-center justify-between rounded-t-xl sticky top-0 z-10"
                style={{ backgroundColor: "#F4F6F9" }}
              >
                <label className="text-sm fw_600">
                  {isEdit ? "Update Function" : "Create Function"}
                </label>
                <ModalCloseIcon className="cursor-pointer" onClick={onClose} />
              </div>

              <div className="p-6 space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-xs fw_500 text-344054 mb-1">
                    Function Name <span className="text-F51D00">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))}
                    placeholder="e.g., inventory_stock_report"
                    className="w-full px-3 py-2 border border-D0D5DD rounded-md text-sm focus:outline-none focus:border-primary"
                  />
                  <p className="text-xsm text-667085 mt-1">Letters, numbers, and underscores only. Will be prefixed automatically.</p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs fw_500 text-344054 mb-1">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe what this function does (optional)"
                    rows={2}
                    className="w-full px-3 py-2 border border-D0D5DD rounded-md text-sm focus:outline-none focus:border-primary resize-none"
                  />
                </div>

                {/* Function Definition */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs fw_500 text-344054">
                      Function Definition <span className="text-F51D00">*</span>
                    </label>
                    {!isEdit && !functionDefinition && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => insertExample(EXAMPLE_SQL)}
                          className="text-xsm text-primary hover:text-blue-700"
                        >
                          SQL example
                        </button>
                        <button
                          onClick={() => insertExample(EXAMPLE_PLPGSQL)}
                          className="text-xsm text-primary hover:text-blue-700"
                        >
                          PL/pgSQL example
                        </button>
                      </div>
                    )}
                  </div>
                  <SqlEditor
                    value={functionDefinition}
                    onChange={setFunctionDefinition}
                    height="300px"
                    schema={schema}
                  />
                  <p className="text-xsm text-667085 mt-1">
                    Write the full function definition: parameters, RETURNS clause, LANGUAGE, and AS $$ body $$.
                    The function name and security settings are added automatically.
                  </p>
                </div>

                {/* Share */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="fnIsShared"
                    checked={isShared}
                    onChange={(e) => setIsShared(e.target.checked)}
                    className="w-4 h-4 text-primary border-D0D5DD rounded focus:ring-primary"
                  />
                  <label htmlFor="fnIsShared" className="text-xs text-344054">
                    Share with store members
                  </label>
                </div>

                {/* Error */}
                {error && (
                  <div className="p-3 bg-red-50 border border-F94B4B rounded-md">
                    <p className="text-xs text-F51D00">{error}</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 flex justify-between border-t border-EAECF0 sticky bottom-0 bg-white">
                <DraftButton
                  text="Validate"
                  onclkFn={handleValidate}
                  isDisabled={!functionDefinition.trim()}
                  loading={validating}
                />
                <div className="flex gap-3">
                  <DraftButton text="Cancel" onclkFn={onClose} />
                  <AddButton
                    text={isEdit ? "Update" : "Create"}
                    prefixIcon={false}
                    onclkFn={handleSave}
                    isDisabled={!name.trim() || !functionDefinition.trim()}
                    loading={loading}
                  />
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default FunctionEditorModal;
